// Вкладка «Інвертори» СЕС Лучки — у тій структурі, якою мислить диспетчер.
//
// Зауваження з зустрічі 16.10: «інвертор 45, 46, 47 — це суто внутрішні
// номери, ні до чого не прив'язані». Тепер кожен інвертор показано під
// позиційним позначенням зі схеми станції (N.U) і з вимикачем, за яким його
// знаходять на полі; Unit ID — лише в деталях, для діагностики.
//
// Ієрархія: КТП (зі своїм EzLogger) → секція шин 0,8 кВ → інвертори. Порядок
// усередині секції — за номером вимикача, тобто так, як вони стоять на шині.
// Адресна відомість — @/scada/equipment/luchky (ТЗ EMS box, ред. 3.1, дод. Б),
// живі значення — /scada/inverters/:assetId/latest, зіставлення за Unit ID.
//
// Правила показу (ТЗ, § 5): невідоме не дорівнює нулю; нульова генерація,
// застарілі дані й втрата звʼязку — три різні стани; сума по групі показує
// окремо достовірну частину й кількість невизначених інверторів.
import { cx } from '@/i18n/cfgText';
import i18n from '@/i18n';
import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { X } from 'lucide-react';
import { SectionCard } from '@/components/scada/kit';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import ChartLegend from '@/components/base/ChartLegend';
import { useChartTheme } from '@/components/base/chartTheme';
import { scada } from '@/scada/tokens';
import { useInverterDay, type InvLatest } from '@/scada/useInverters';
import {
  LUCHKY_INVERTERS, LUCHKY_KTPS, LUCHKY_INVERTER_MODEL, LUCHKY_PLC_CHANNELS, LUCHKY_SOURCE, ktpSkid,
  type LuchkyInverter,
} from '@/scada/equipment/luchky';
import {
  useSharedPoll, cadenceOf, linkOf, ageSecOfLocal, clockOfLocal, fmtAge, kw0, n1, LINK_STYLE,
} from '@/scada/live';

type InvState = 'ok' | 'idle' | 'warning' | 'alarm' | 'stale' | 'lost' | 'nodata';

const INV_STYLE: Record<InvState, { c: string; label: string; issue: boolean }> = {
  ok: { c: scada.state.ok, get label() { return i18n.t('ak_scada2_running'); }, issue: false },
  idle: { c: scada.textMuted, get label() { return i18n.t('ak_scada2_no_generation'); }, issue: false },
  warning: { c: scada.state.warning, get label() { return i18n.t('ak_scada2_warning'); }, issue: true },
  alarm: { c: scada.state.alarm, get label() { return i18n.t('ak_scada2_alarm'); }, issue: true },
  stale: { c: scada.state.warning, get label() { return i18n.t('ak_scada2_data_is_getting_stale'); }, issue: true },
  lost: { c: scada.state.offline, get label() { return i18n.t('ak_scada2_no_link'); }, issue: true },
  nodata: { c: scada.state.offline, get label() { return i18n.t('ak_scada2_no_data'); }, issue: true },
};

const SECTION_LABEL: Record<'D1' | 'D2', string> = { get D1() { return i18n.t('ak_scada2_bus_i_section_d1'); }, get D2() { return i18n.t('ak_scada2_bus_ii_section_d2'); } };

const CAD = cadenceOf('luchky');
const breakerNo = (b: string) => Number(b.split('QF')[1] ?? 0);

function classify(live: InvLatest | undefined): InvState {
  if (!live) return 'nodata';
  const link = linkOf(ageSecOfLocal(live.readAtLocal), CAD);
  if (link === 'lost' || link === 'unknown') return 'lost';
  if (link === 'stale') return 'stale';
  const s = (live.status ?? '').toLowerCase();
  if (/(fault|alarm|error|err)/.test(s)) return 'alarm';
  if (s && !['ok', 'normal', 'running', 'on'].includes(s)) return 'warning';
  if ((live.pacKw ?? 0) < 0.5) return 'idle';
  return 'ok';
}

/** Потужність, яку можна чесно додати до суми: лише зі свіжих знімків. */
const trustedKw = (st: InvState, live?: InvLatest) =>
  live && (st === 'ok' || st === 'idle' || st === 'warning' || st === 'alarm') ? (live.pacKw ?? 0) : null;

interface Row { inv: LuchkyInverter; live?: InvLatest; st: InvState }

function summarize(rows: Row[]) {
  let kw = 0, trusted = 0, unknown = 0, online = 0;
  for (const r of rows) {
    const v = trustedKw(r.st, r.live);
    if (v == null) unknown += 1; else { kw += v; trusted += 1; }
    if (r.st !== 'lost' && r.st !== 'nodata') online += 1;
  }
  return { kw, trusted, unknown, online, total: rows.length };
}

function SumLine({ s, capKw }: { s: ReturnType<typeof summarize>; capKw?: number }) {
  return (
    <span style={{ color: scada.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>
      <b style={{ color: s.online === s.total ? scada.state.ok : s.online === 0 ? scada.state.offline : scada.state.warning }}>{s.online}/{s.total}</b>
      {' · '}<b style={{ color: scada.text }}>{kw0(s.kw)}</b> {i18n.t('ak_scada2_kw')}
      {capKw ? i18n.t('ak_scada2_of', { n: kw0(capKw) }) : ''}
      {s.unknown > 0 && <span style={{ color: scada.state.warning }}>{i18n.t('ak_scada2_undetermined', { n: s.unknown })}</span>}
    </span>
  );
}

function InvTile({ r, selected, onClick }: { r: Row; selected: boolean; onClick: () => void }) {
  const sty = INV_STYLE[r.st];
  const pct = r.live?.percentOfNominal ?? (r.live?.pacKw != null ? (r.live.pacKw / LUCHKY_INVERTER_MODEL.nominalKw) * 100 : null);
  const fresh = trustedKw(r.st, r.live) != null;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${r.inv.designation} · ${r.inv.breaker} · ${sty.label}`}
      style={{
        textAlign: 'left', background: selected ? scada.panel2 : 'transparent',
        // Сторони рамки окремо: змішування border і borderLeft React вважає
        // конфліктом і при перерендері може скинути колір стану.
        borderTop: `1px solid ${selected ? scada.borderStrong : scada.border}`,
        borderRight: `1px solid ${selected ? scada.borderStrong : scada.border}`,
        borderBottom: `1px solid ${selected ? scada.borderStrong : scada.border}`,
        borderLeft: `3px solid ${sty.c}`, borderRadius: 8, padding: '6px 8px', cursor: 'pointer', minWidth: 0,
      }}
    >
      <div className="flex items-baseline justify-between" style={{ gap: 4 }}>
        <span style={{ color: scada.text, fontSize: 13, fontWeight: 700 }}>{r.inv.designation}</span>
        <span style={{ color: scada.textMuted, fontSize: 10 }}>{r.inv.breaker}</span>
      </div>
      <div style={{ color: fresh ? scada.text : scada.textMuted, fontSize: 12, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
        {r.st === 'lost' || r.st === 'nodata' ? '—' : `${r.st === 'stale' ? '~' : ''}${kw0(r.live?.pacKw)} ${i18n.t('ak_scada2_kw')}`}
      </div>
      <div style={{ height: 3, borderRadius: 2, background: scada.border, marginTop: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${fresh && pct != null ? Math.max(0, Math.min(100, pct)) : 0}%`, background: sty.c }} />
      </div>
    </button>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: scada.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ color: scada.text, fontSize: 13, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

function InverterDetail({ assetId, r, onClose }: { assetId: string; r: Row; onClose: () => void }) {
  const ct = useChartTheme();
  const { series } = useInverterDay(assetId);
  const pts = useMemo(() => series?.find((s) => s.unitId === r.inv.unitId)?.points ?? [], [series, r.inv.unitId]);
  const ktp = LUCHKY_KTPS.find((k) => k.ktp === r.inv.ktp)!;
  const sty = INV_STYLE[r.st];
  const age = ageSecOfLocal(r.live?.readAtLocal);
  return (
    <div style={{ border: `1px solid ${scada.borderStrong}`, borderRadius: 10, padding: 12, marginTop: 12, background: scada.panel2 }}>
      <div className="flex items-start justify-between" style={{ gap: 8 }}>
        <div>
          <div style={{ color: scada.text, fontSize: 15, fontWeight: 700 }}>
            {i18n.t('ak_scada2_inverter')} {r.inv.designation}
            <span style={{ color: sty.c, fontSize: 12, fontWeight: 600, marginLeft: 8 }}>● {sty.label}</span>
          </div>
          <div style={{ color: scada.textMuted, fontSize: 11 }}>
            {cx(ktp.name)} · {SECTION_LABEL[r.inv.group]} · {i18n.t('ak_scada2_breaker')} {r.inv.breaker}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label={i18n.t('ak_scada2_close')} style={{ background: 'transparent', border: 'none', color: scada.textDim, cursor: 'pointer', padding: 4 }}>
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ marginTop: 10 }}>
        <DetailField label="P AC" value={r.live ? `${n1(r.live.pacKw)} ${i18n.t('ak_scada2_kw')}` : '—'} />
        <DetailField label="P DC" value={r.live ? `${n1(r.live.ppvKw)} ${i18n.t('ak_scada2_kw')}` : '—'} />
        <DetailField label={i18n.t('ak_scada2_of_nominal')} value={r.live?.percentOfNominal != null ? `${n1(r.live.percentOfNominal)} %` : '—'} />
        <DetailField label={i18n.t('ak_scada2_frequency')} value={r.live?.facHz != null ? `${r.live.facHz.toFixed(2)} ${i18n.t('ak_scada2_hz')}` : '—'} />
        <DetailField label={i18n.t('ak_scada2_last_data')} value={r.live ? `${clockOfLocal(r.live.readAtLocal)} · ${fmtAge(age)}` : i18n.t('ak_scada2_none')} />
        <DetailField label={i18n.t('ak_scada2_status_raw')} value={r.live ? `${r.live.status}${r.live.statusRaw != null ? ` · ${r.live.statusRaw}` : ''}` : '—'} />
        <DetailField label={i18n.t('ak_scada2_model')} value={`${LUCHKY_INVERTER_MODEL.model} · ${LUCHKY_INVERTER_MODEL.nominalKw} ${i18n.t('ak_scada2_kw')}`} />
        <DetailField label={i18n.t('ak_scada2_s_n_per_spec')} value={r.inv.serial} />
        <DetailField label={i18n.t('ak_scada2_logger')} value={`${ktp.loggerName} · ${ktp.loggerIp}`} />
        <DetailField label="Unit ID" value={String(r.inv.unitId)} />
        <DetailField label={i18n.t('ak_scada2_plc_channel')} value={LUCHKY_PLC_CHANNELS[r.inv.group]} />
        <DetailField label={i18n.t('ak_scada2_factory_interface')} value={i18n.t('ak_scada2_ezlogger_from_the_station_network', { ip: ktp.loggerIp })} />
      </div>

      <div style={{ marginTop: 12 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
          <span style={{ color: scada.textDim, fontSize: 11, fontWeight: 600 }}>{i18n.t('ak_scada2_power_per_day_kw')}</span>
          <ChartLegend items={[{ label: 'AC', color: ct.series.ok }, { label: 'DC', color: ct.series.s2 }]} />
        </div>
        <div style={{ height: 170 }}>
          {pts.length === 0 ? (
            <div className="flex items-center justify-center" style={{ height: '100%', color: scada.textMuted, fontSize: 12 }}>
              {series ? i18n.t('ak_scada2_no_daily_data_for_this_inverter') : i18n.t('ak_scada2_loading')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={pts} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid {...ct.gridProps} />
                <XAxis dataKey="t" {...ct.axis} minTickGap={28} />
                <YAxis {...ct.axis} width={44} />
                <Tooltip {...ct.tooltip} />
                <Line dataKey="pac" name="AC" stroke={ct.series.ok} {...ct.line} />
                <Line dataKey="ppv" name="DC" stroke={ct.series.s2} {...ct.lineThin} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LuchkyInverterBoard({ assetId }: { assetId: string }) {
  const { data, err } = useSharedPoll<{ inverters: InvLatest[] }>(`/scada/inverters/${assetId}/latest`, 30000);
  const [ktpFilter, setKtpFilter] = useState<'all' | '1' | '2' | '3'>('all');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState<number | null>(null);

  const byUnit = useMemo(() => new Map((data?.inverters ?? []).map((i) => [i.unitId, i])), [data]);
  const rows: Row[] = useMemo(
    () => LUCHKY_INVERTERS.map((inv) => { const live = byUnit.get(inv.unitId); return { inv, live, st: classify(live) }; }),
    [byUnit],
  );
  // Дані, що прийшли від колектора, але відсутні в адресній відомості, — це
  // розбіжність конфігурації; показуємо її, а не мовчки відкидаємо.
  const unknownUnits = useMemo(() => {
    const known = new Set(LUCHKY_INVERTERS.map((i) => i.unitId));
    return (data?.inverters ?? []).filter((i) => !known.has(i.unitId)).map((i) => i.unitId);
  }, [data]);

  const total = summarize(rows);
  const issues = rows.filter((r) => INV_STYLE[r.st].issue).length;

  const q = query.trim().toLowerCase();
  const visible = (r: Row) =>
    (ktpFilter === 'all' || String(r.inv.ktp) === ktpFilter)
    && (!onlyIssues || INV_STYLE[r.st].issue)
    && (!q || [r.inv.designation, r.inv.breaker, r.inv.serial, String(r.inv.unitId)].some((v) => v.toLowerCase().includes(q)));

  if (!data && !err) {
    return <SectionCard title={i18n.t('ak_scada2_inverters')}><span style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada2_loading')}</span></SectionCard>;
  }

  const inputStyle = {
    background: scada.panel, border: `1px solid ${scada.border}`, color: scada.text,
    borderRadius: 10, padding: '0 12px', height: 36, fontSize: 13, minWidth: 0,
  } as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionCard
        title={i18n.t('ak_scada2_inverters_by_ktp_and_section')}
        subtitle={i18n.t('ak_scada2_per_the_addressing_table', { n: total.total, src: cx(LUCHKY_SOURCE) })}
        right={<SumLine s={total} />}
      >
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <SegmentedTabs
            variant="outline"
            items={[
              { key: 'all', label: i18n.t('ak_scada2_all_ktp') },
              ...LUCHKY_KTPS.map((k) => ({ key: String(k.ktp), label: cx(k.name) })),
            ]}
            activeKey={ktpFilter}
            onSelect={(k) => setKtpFilter(k as typeof ktpFilter)}
          />
          <SegmentedTabs
            variant="outline"
            items={[
              { key: 'all', label: i18n.t('ak_scada2_all') },
              { key: 'issues', label: i18n.t('ak_scada2_deviations', { n: issues }) },
            ]}
            activeKey={onlyIssues ? 'issues' : 'all'}
            onSelect={(k) => setOnlyIssues(k === 'issues')}
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={i18n.t('ak_scada2_search_45_u_2_qf6_s_n')}
            aria-label={i18n.t('ak_scada2_inverter_search')}
            style={{ ...inputStyle, flex: '1 1 180px', maxWidth: 260 }}
          />
        </div>
        {err && (
          <div style={{ color: scada.state.warning, fontSize: 12, marginTop: 10 }}>
            {i18n.t('ak_scada2_the_last_poll_failed_the_previous_snap')}
          </div>
        )}
        {unknownUnits.length > 0 && (
          <div style={{ color: scada.state.warning, fontSize: 12, marginTop: 10 }}>
            {i18n.t('ak_scada2_the_collector_returns_unit_ids_that_ar', { ids: unknownUnits.join(', ') })}
          </div>
        )}
      </SectionCard>

      {LUCHKY_KTPS.filter((k) => ktpFilter === 'all' || String(k.ktp) === ktpFilter).map((k) => {
        const ktpRows = rows.filter((r) => r.inv.ktp === k.ktp);
        const s = summarize(ktpRows);
        const skid = ktpSkid(ktpRows.length);
        // Стан логера — за найсвіжішим знімком його інверторів: якщо мовчать
        // усі, це відмова логера (або каналу до нього), а не 20 нульових інверторів.
        const freshest = ktpRows.map((r) => ageSecOfLocal(r.live?.readAtLocal)).filter((a): a is number => a != null).sort((a, b) => a - b)[0] ?? null;
        const loggerLink = linkOf(freshest, CAD);
        const selRow = ktpRows.find((r) => r.inv.unitId === sel) ?? null;
        const sections = (['D1', 'D2'] as const).map((g) => ({
          g,
          rows: ktpRows.filter((r) => r.inv.group === g).sort((a, b) => breakerNo(a.inv.breaker) - breakerNo(b.inv.breaker)),
        }));
        const anyVisible = ktpRows.some(visible);
        if (!anyVisible && (onlyIssues || q)) return null;

        return (
          <SectionCard
            key={k.ktp}
            title={`${cx(k.name)} · ${skid.model}`}
            subtitle={`${cx(skid.transformer)} · ${k.loggerName} ${k.loggerIp} — ${LINK_STYLE[loggerLink].label}${freshest != null ? `, ${fmtAge(freshest)}` : ''}`}
            right={<SumLine s={s} capKw={skid.exportKw} />}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sections.map(({ g, rows: secRows }) => {
                const ss = summarize(secRows);
                const shown = secRows.filter(visible);
                return (
                  <div key={g} style={{ minWidth: 0 }}>
                    <div className="flex items-center justify-between flex-wrap" style={{ gap: 6, marginBottom: 8 }}>
                      <span style={{ color: scada.textDim, fontSize: 12, fontWeight: 600 }}>
                        {SECTION_LABEL[g]} <span style={{ color: scada.textMuted, fontWeight: 400 }}>{i18n.t('ak_scada2_breakers_qf_in_bus_order', { n: g === 'D1' ? '1' : '2' })}</span>
                      </span>
                      <SumLine s={ss} />
                    </div>
                    {shown.length === 0 ? (
                      <div style={{ color: scada.textMuted, fontSize: 12 }}>{i18n.t('ak_scada2_no_inverters_match_the_filter')}</div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-6 gap-1.5">
                        {shown.map((r) => (
                          <InvTile key={r.inv.unitId} r={r} selected={sel === r.inv.unitId} onClick={() => setSel(sel === r.inv.unitId ? null : r.inv.unitId)} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selRow && <InverterDetail assetId={assetId} r={selRow} onClose={() => setSel(null)} />}
          </SectionCard>
        );
      })}

      <div className="flex flex-wrap" style={{ gap: 14, color: scada.textMuted, fontSize: 11 }}>
        {(Object.keys(INV_STYLE) as InvState[]).map((st) => (
          <span key={st} className="flex items-center" style={{ gap: 5 }}>
            <span style={{ width: 10, height: 3, borderRadius: 2, background: INV_STYLE[st].c }} />{INV_STYLE[st].label}
          </span>
        ))}
        <span>{i18n.t('ak_scada2_value_from_a_stale_snapshot_not_includ')}</span>
      </div>
    </div>
  );
}
